/**
 * Фабрики ресурсов API: по одной на каждое умение — список, карточка, запись, удаление.
 *
 * Двадцать с лишним объектов API повторяют одни и те же четыре строки, но единой CRUD-фабрикой их
 * не накрыть: наборы операций у ресурсов разные, и различия эти не случайные, а содержательные.
 * У типов контейнеров удаления нет вовсе — тип деактивируют правкой; у ТТХ нет ни карточки, ни
 * удаления; у объектов удаление возвращает DTO (карточка переехала в архив), а у контрагентов —
 * `{ ok }`. Фабрика «на всё сразу» выдала бы каждому ресурсу ручки, которых у него нет: вызов
 * такой ручки компилируется, уходит на сервер и отвечает 404 — то есть ошибка обнаруживается на
 * экране, а не при сборке.
 *
 * Отсюда правило: **фабрика описывает то, что у ресурса есть, а не то, что у ресурса бывает.**
 * Слайс сущности собирает свой объект API из нужных умений расширением:
 *
 * ```ts
 * export const objectsApi = {
 *   ...createListApi<ObjectDto>(PATH),
 *   ...createWriteApi<ObjectDto, CreateObjectInput, UpdateObjectInput>(PATH),
 *   ...createRemoveApi<ObjectDto>(PATH),
 *   purge: (id: string) => apiFetch<{ ok: boolean }>(`${PATH}/${id}/purge`, { method: 'DELETE' }),
 * };
 * ```
 *
 * Нестандартные ручки (`restore`, `purge`, `resolve`, `summary`, `changeStatus`) остаются в слайсе
 * явными. Дело не только в их числе: это слова портала, а не HTTP. Фабрика, знающая про «удалить
 * насовсем» и «вернуть из архива», знала бы правила портала — а нижнему слою их знать нечем.
 */
import { apiFetch } from './http';

/** Параметры списка, как их собирает экран: фильтры, страница, сортировка. */
export type Query = Record<string, unknown>;

/**
 * Страница списка. Объявлена здесь, а не взята из общих контрактов: `shared` не знает правил
 * портала, и линт границ запрещает ему пакет контрактов. Форма совпадает с контрактной один в один,
 * поэтому слайсу ничего не мешает объявлять свой список типом из контрактов — для компилятора это
 * один и тот же тип.
 */
export interface ListResult<TDto> {
  items: TDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** Список: `GET /ресурс?параметры`. */
export const createListApi = <TDto>(path: string) => ({
  list: (query: Query) => apiFetch<ListResult<TDto>>(path, { query }),
});

/**
 * Карточка: `GET /ресурс/:id`. Умение редкое — его заводят там, где карточку открывают отдельным
 * маршрутом, а не достают из уже загруженного списка.
 */
export const createGetApi = <TDto>(path: string) => ({
  get: (id: string) => apiFetch<TDto>(`${path}/${id}`),
});

/**
 * Запись: `POST /ресурс` и `PATCH /ресурс/:id`. Вместе, потому что порознь не встречаются — где
 * заводят, там и правят.
 *
 * Три параметра типа, а не один: тело заведения и тело правки — разные типы (у правки поля
 * необязательны, а часть неизменяема вовсе), и общий `Partial<TDto>` разрешил бы менять то, что
 * сервер менять не даст.
 */
export const createWriteApi = <TDto, TCreate, TUpdate>(path: string) => ({
  create: (body: TCreate) => apiFetch<TDto>(path, { method: 'POST', body }),
  update: (id: string, body: TUpdate) => apiFetch<TDto>(`${path}/${id}`, { method: 'PATCH', body }),
});

/**
 * Удаление: `DELETE /ресурс/:id`. Тип ответа задаёт ресурс, и значения по умолчанию у него нет
 * намеренно: сервер отвечает то `{ ok }`, то карточкой, уехавшей в архив, то `{ ok, mode }` —
 * заявка либо помечается удалённой, либо сносится совсем, — а то и пустым телом. Умолчание
 * `{ ok: boolean }` пришлось бы обходить у половины ресурсов, и там, где его забыли обойти, тип
 * врал бы про ответ молча.
 */
export const createRemoveApi = <TResult>(path: string) => ({
  remove: (id: string) => apiFetch<TResult>(`${path}/${id}`, { method: 'DELETE' }),
});
